import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db, auth } from "../services/firebase";
import { useUserData } from "../hooks/useUserData";

export default function RequestList() {
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState(null);
  const [user, userLoading] = useUserData();
  const navigate = useNavigate();

  // Listen to requests made by the current user only
  useEffect(() => {
    if (!user) {
      setRequestsLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, "requests"),
        where("requesterId", "==", user.uid)
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const requestsData = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt,
              startDate: data.startDate,
              endDate: data.endDate,
              lastUpdated: data.lastUpdated
            };
          });

          // Sort by createdAt descending in JavaScript
          requestsData.sort((a, b) => {
            const aTime = a.createdAt?.seconds || a.createdAt;
            const bTime = b.createdAt?.seconds || b.createdAt;
            return bTime - aTime;
          });

          setRequests(requestsData);
          setRequestsLoading(false);
          setError(null);
        },
        (error) => {
          console.error("Error fetching requests:", error);
          setError("Failed to load requests: " + error.message);
          setRequestsLoading(false);
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error("Error setting up Firestore query:", error);
      setError("Failed to set up request listener: " + error.message);
      setRequestsLoading(false);
    }
  }, [user]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending": return "bg-yellow-100 text-yellow-700";
      case "approved": return "bg-green-100 text-green-700";
      case "rejected": return "bg-red-100 text-red-700";
      case "completed": return "bg-blue-100 text-blue-700";
      case "reviewed": 
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency?.toLowerCase()) {
      case "urgent": return "text-red-600 font-bold";
      case "high": return "text-orange-600 font-semibold";
      case "normal": return "text-blue-600";
      default: return "text-gray-600";
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    
    // Handle Firestore Timestamp
    if (dateValue.seconds) {
      return new Date(dateValue.seconds * 1000).toLocaleDateString();
    }
    
    // Handle string date (like "2025-10-31")
    if (typeof dateValue === 'string') {
      return new Date(dateValue).toLocaleDateString();
    }
    
    return "Invalid date";
  };

  const filteredRequests =
    filter === "all" ? requests : requests.filter((r) => r.status?.toLowerCase() === filter.toLowerCase());

  if (userLoading || requestsLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="pt-20 flex flex-col justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 mt-2">Loading your requests...</p>
          {error && <div className="text-red-500 mt-2 text-sm text-center max-w-xs">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-gray-50 min-h-screen">
      <div className="pt-20 px-2 sm:px-4">
        {/* Header */}
        <div className="mb-3 sm:mb-6">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">My Requests</h1>
        </div>

        {/* Filter Buttons */}
        <div className="flex space-x-2 overflow-x-auto pb-2 sm:pb-3 -mx-2 sm:-mx-4 px-2 sm:px-4">
          {["all", "pending", "approved", "rejected", "completed"].map((status) => {
            const count = status === "all"
              ? requests.length
              : requests.filter(r => r.status?.toLowerCase() === status.toLowerCase()).length;

            return (
              <button
                key={status}
                className={`flex-shrink-0 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition whitespace-nowrap ${
                  filter === status ? "bg-orange-600 text-white shadow-md" : "bg-white text-gray-700 border border-gray-300"
                }`}
                onClick={() => setFilter(status)}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
                <span className="ml-1 hidden sm:inline">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Requests Count */}
        <div className="mb-3 sm:mb-4 text-xs sm:text-sm text-gray-600 px-2 sm:px-0">
          Showing {filteredRequests.length} of {requests.length} requests
          {filter !== "all" && ` (${filter} only)`}
        </div>

        {filteredRequests.length === 0 ? (
          <div className="text-center text-gray-500 mt-6 sm:mt-10 p-4 sm:p-6 bg-white rounded-lg sm:rounded-xl shadow mx-2 sm:mx-0">
            <p className="text-base sm:text-lg font-semibold mb-2">
              No {filter !== 'all' ? filter : ''} requests found.
            </p>
            <button
              onClick={() => navigate('/student/item-request')}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition"
            >
              Submit Request
            </button>
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-4 px-2 sm:px-0">
            {filteredRequests.map((req) => (
              <div
                key={req.id}
                className="bg-white p-3 sm:p-4 rounded-lg sm:rounded-xl shadow-md sm:shadow-lg border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-base sm:text-lg mb-1 truncate">
                      {req.assetName || "Unspecified Asset"}
                    </h3>
                    {req.assetCategory && (
                      <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                        {req.assetCategory === "ict-equipment" ? "ICT Equipment" : req.assetCategory}
                      </span>
                    )}
                  </div>
                  <span
                    className={`ml-2 flex-shrink-0 px-2 py-1 sm:px-3 text-xs rounded-full font-bold uppercase ${getStatusColor(req.status)}`}
                  >
                    {req.status || "Unknown"}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm text-gray-700 mb-3">
                  <div>
                    <strong className="font-medium">Type:</strong>
                    <span className="capitalize ml-1">{req.type || "Unknown"}</span>
                  </div>
                  <div>
                    <strong className="font-medium">Urgency:</strong>
                    <span className={`ml-1 ${getUrgencyColor(req.urgency)}`}>
                      {req.urgency || "Normal"}
                    </span>
                  </div>
                </div>

                {(req.type?.toLowerCase() === 'borrow' || req.startDate) && (
                  <div className="text-xs sm:text-sm text-gray-700 border-t border-gray-100 pt-2 mb-3">
                    <p>
                      <strong className="font-medium">Period:</strong>{" "}
                      <span className="block sm:inline">{formatDate(req.startDate)} - {formatDate(req.endDate)}</span>
                    </p>
                  </div>
                )}

                <div className="text-xs sm:text-sm text-gray-600 border-t border-gray-100 pt-2">
                  <strong className="font-medium text-gray-700">Details:</strong>
                  <p className="mt-1 leading-relaxed">{req.reason || "No details provided"}</p>
                </div>

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-3 pt-2 border-t border-gray-100 gap-1">
                  <span className="text-xs text-gray-400">
                    Submitted: {req.createdAt ? formatDate(req.createdAt) : "Unknown date"}
                  </span>
                  {req.lastUpdated && req.lastUpdated.seconds !== req.createdAt?.seconds && (
                    <span className="text-xs text-gray-400">
                      Updated: {formatDate(req.lastUpdated)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
